/**
 * PerfDayReportModal — Iter 400.18
 *
 * Full detailed log-sheet view for a single day at a specific site.
 * Shows every reading captured that day grouped by equipment, with:
 *   • parameter name + unit + target range
 *   • captured value + status pill
 *   • WHO captured it (name + role) and WHEN (HH:MM)
 *   • proof photos (click to enlarge)
 *   • notes + edit history
 *
 * "Download PDF" button hits the matching `/perf/day-report/pdf`
 * endpoint so the same view can be shared / archived offline.
 */
import { useEffect, useState } from "react";
import {
  Loader2,
  X,
  Download,
  Camera,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Clock,
  User,
} from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { uploadUrl } from "../../lib/uploads";
import { toast } from "../ui/sonner";

const STATUS_META = {
  ok:      { Icon: CheckCircle2, cls: "text-emerald-300 bg-emerald-500/10 border-emerald-400/40" },
  warn:    { Icon: AlertTriangle, cls: "text-amber-300 bg-amber-500/10 border-amber-400/40" },
  alarm:   { Icon: AlertCircle, cls: "text-rose-300 bg-rose-500/10 border-rose-400/40" },
  unknown: { Icon: Clock, cls: "text-[#a0aabf] bg-white/[0.03] border-white/10" },
  // Iter 418.h — un-captured placeholder rows (perf_day_report now
  // synthesises these for every parameter that wasn't logged).
  not_captured: { Icon: AlertTriangle, cls: "text-amber-300/80 bg-amber-500/5 border-amber-400/30" },
};

function fmtTarget(t) {
  if (!t || !t.kind) return "—";
  const u = t.unit || "";
  const k = t.kind;
  if (k === "exact") return `= ${t.value ?? "?"} ${u}`.trim();
  if (k === "min") return `≥ ${t.min ?? "?"} ${u}`.trim();
  if (k === "max") return `≤ ${t.max ?? "?"} ${u}`.trim();
  if (k === "range") return `${t.min ?? "?"}–${t.max ?? "?"} ${u}`.trim();
  return "—";
}

export default function PerfDayReportModal({ siteId, date, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const qs = new URLSearchParams({ date });
        if (siteId) qs.set("site_id", siteId);
        const { data: d } = await api.get(`/perf/day-report?${qs.toString()}`);
        setData(d);
      } catch (e) {
        setErr(formatErr(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [siteId, date]);

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const qs = new URLSearchParams({ date });
      if (siteId) qs.set("site_id", siteId);
      const resp = await api.get(`/perf/day-report/pdf?${qs.toString()}`, {
        responseType: "blob",
        timeout: 60_000,
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Iter 418.i (Dirk, 2026-02-28) — Mobile Safari/Chrome silently
      // block `window.open(url, "_blank")` when the call happens AFTER
      // an `await` (they consider it a non-user-initiated popup), which
      // is why production users saw "generating" then nothing. Instead
      // we synthesise an anchor with the `download` attribute — every
      // mobile browser honours this as a real download intent, and on
      // desktop it still opens in a background tab if the user prefers.
      const fname = `perf-day-report-${date}.pdf`;
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      a.rel = "noopener noreferrer";
      // For iOS Safari the anchor MUST be attached to the DOM before
      // click() will trigger the native download prompt.
      document.body.appendChild(a);
      a.click();
      // Small delay before removal so mobile Safari has time to grab
      // the blob reference — otherwise it sometimes silently cancels.
      setTimeout(() => {
        try { document.body.removeChild(a); } catch (_) { /* noop */ }
        URL.revokeObjectURL(url);
      }, 3000);
      toast.success("Day report PDF ready.");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setDownloading(false);
    }
  };

  const s = data?.summary || {};

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4"
      onClick={onClose}
      data-testid="perf-day-report-modal"
    >
      <div
        className="hv-card w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-4 border-b border-white/5">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">
              Day report · full log sheet
            </div>
            <div className="text-lg font-semibold truncate">
              {date}
              {data?.site?.name && (
                <span className="text-[#a0aabf] text-sm ml-2">
                  · {data.site.name}
                </span>
              )}
            </div>
            {data && (
              <div className="text-[11px] text-[#647087] mt-0.5 flex flex-wrap items-center gap-2">
                <span>Total <b className="text-white">{s.total || 0}</b></span>
                <span className="text-emerald-300">OK {s.ok || 0}</span>
                <span className="text-amber-300">Warn {s.warn || 0}</span>
                <span className="text-rose-300">Alarm {s.alarm || 0}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={download}
              disabled={downloading || loading || !data}
              className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
              data-testid="perf-day-report-download-pdf"
            >
              {downloading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              <span>{downloading ? "Generating…" : "Export PDF"}</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded hover:bg-white/5 text-[#a0aabf]"
              aria-label="Close"
              data-testid="perf-day-report-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-16 text-[#a0aabf]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading day report…
            </div>
          )}
          {err && !loading && (
            <div className="hv-card border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">
              {err}
            </div>
          )}
          {!loading && !err && data && data.groups.length === 0 && (
            <div className="text-center py-16 text-[#647087]">
              No readings were captured on this day.
            </div>
          )}
          {!loading && !err && data && data.groups.map((g) => (
            <div key={g.equipment_id} className="mb-6" data-testid={`perf-day-report-group-${g.equipment_id}`}>
              <h3 className="text-sm font-semibold text-[#22d3ee] mb-2 uppercase tracking-wider">
                {g.equipment_name}
              </h3>
              <div className="space-y-2">
                {g.rows.map((r) => {
                  const meta = STATUS_META[r.status || "unknown"] || STATUS_META.unknown;
                  const Icon = meta.Icon;
                  return (
                    <div
                      key={r.id}
                      className="hv-card p-3 flex flex-col sm:flex-row gap-3"
                      data-testid={`perf-day-report-row-${r.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 flex-wrap">
                          <span className="text-sm font-semibold">
                            {r.parameter_name || "?"}
                            {r.parameter_unit && (
                              <span className="text-[#647087] font-normal ml-1">
                                ({r.parameter_unit})
                              </span>
                            )}
                            {r.parameter_mandatory && (
                              <span className="text-rose-300 ml-1" title="Mandatory">*</span>
                            )}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-mono ${meta.cls}`}>
                            <Icon className="w-3 h-3" />
                            {/* Iter 418.h — display "NOT CAPTURED" instead
                                of "—" for synthesised placeholder rows so
                                clients can spot gaps at a glance. */}
                            {r.status === "not_captured"
                              ? <span className="text-[10px] uppercase tracking-wider">Not captured</span>
                              : (r.value ?? "—")}
                          </span>
                          {r.averaged && (
                            <span
                              className="text-[9px] uppercase tracking-wider text-orange-300 border border-orange-400/40 bg-orange-500/10 rounded px-1.5 py-0.5"
                              title={`Averaged over ${r.averaged_over_days} days since ${r.averaged_from_date}`}
                            >
                              ~avg
                            </span>
                          )}
                          {r.pending_edit_request && (
                            <span
                              className="text-[9px] uppercase tracking-wider text-amber-300 border border-amber-400/40 bg-amber-500/10 rounded px-1.5 py-0.5"
                              title={`Pending approval · ${r.pending_edit_request.reason || ""}`}
                            >
                              pending
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-[#647087] mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {(r.captured_at || "").slice(11, 16) || "—:—"}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {r.captured_by_name || "?"}
                            {r.captured_by_role && (
                              <span className="text-[9px] uppercase text-[#a0aabf]">
                                · {r.captured_by_role}
                              </span>
                            )}
                          </span>
                          <span className="text-[10px]">
                            Target: {fmtTarget(r.parameter_target)}
                          </span>
                        </div>
                        {r.notes && (
                          <div className="text-[11px] text-[#a0aabf] bg-white/[0.02] border border-white/5 rounded p-1.5 mt-2">
                            <span className="text-[9px] uppercase tracking-wider text-[#647087]">Notes</span>
                            <div className="mt-0.5">{r.notes}</div>
                          </div>
                        )}
                        {r.edit_history && r.edit_history.length > 0 && (
                          <div className="text-[11px] text-[#a78bfa] bg-[#a78bfa]/5 border border-[#a78bfa]/20 rounded p-1.5 mt-2">
                            <span className="text-[9px] uppercase tracking-wider text-[#a78bfa]">
                              Edit history · {r.edit_history.length}
                            </span>
                            {r.edit_history.slice(-3).map((ed, i) => (
                              <div key={i} className="mt-0.5">
                                {(ed.at || "").slice(0, 16).replace("T", " ")} ·{" "}
                                <b>{ed.by_name || "?"}</b>:{" "}
                                <span className="line-through text-rose-300 font-mono">
                                  {ed.before?.value ?? "—"}
                                </span>{" "}
                                →{" "}
                                <span className="text-emerald-300 font-mono">
                                  {ed.after?.value ?? "—"}
                                </span>
                                {ed.reason && (
                                  <span className="text-[#a0aabf]"> · &ldquo;{ed.reason}&rdquo;</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Iter 401.5 — Route each proof photo through
                          the authenticated /api/uploads/<id> proxy
                          (same helper the customer Gallery uses).
                          Previously we rendered the raw R2 presigned
                          URL from `photo_urls`, which broke as soon
                          as (a) the URL expired (default 1 h TTL) or
                          (b) the R2 bucket's CORS rejected the
                          browser origin. Falling back to
                          `photo_upload_ids` (bare IDs) + uploadUrl()
                          gives us a stable, auth-scoped fetch that
                          matches how proof photos already work in
                          the Performance folder view. */}
                      {(r.photo_upload_ids || []).length > 0 && (
                        <div className="flex gap-1.5 flex-wrap items-start shrink-0">
                          {(r.photo_upload_ids || []).map((uid, i) => {
                            const url = uploadUrl(uid);
                            return (
                              <button
                                key={uid || i}
                                type="button"
                                onClick={() => setPhotoUrl(url)}
                                className="w-16 h-16 sm:w-20 sm:h-20 rounded border border-white/10 hover:border-[#22d3ee] overflow-hidden bg-white/[0.02] transition"
                                data-testid={`perf-day-report-photo-${r.id}-${i}`}
                                title="Click to enlarge"
                              >
                                <img src={url} alt="proof" className="w-full h-full object-cover" />
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {(r.photo_upload_ids || []).length === 0 && (
                        <div className="text-[10px] text-[#647087] shrink-0 self-center inline-flex items-center gap-1">
                          <Camera className="w-3 h-3 opacity-40" />
                          No photos
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Iter 418.h (Dirk, 2026-02-28) — Photo lightbox now has an
          explicit Close button (top-right) + Escape key handler, so
          users on mobile with no obvious click-outside area can get
          back to the day report cleanly. Matches the JC lightbox UX. */}
      {photoUrl && (
        <PerfPhotoLightbox url={photoUrl} onClose={() => setPhotoUrl(null)} />
      )}
    </div>
  );
}

function PerfPhotoLightbox({ url, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[9500] flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
      data-testid="perf-photo-lightbox"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm backdrop-blur"
        data-testid="perf-photo-lightbox-close"
        title="Close (Esc) — return to day report"
      >
        <X className="w-4 h-4" />
        <span className="hidden sm:inline text-xs uppercase tracking-wider">Close</span>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white/80 text-xs backdrop-blur"
        title="Back to day report"
        data-testid="perf-photo-lightbox-back"
      >
        ← <span className="uppercase tracking-wider">Day report</span>
      </button>
      <img
        src={url}
        alt="proof enlarged"
        className="max-w-full max-h-full rounded shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}
